import { useEffect, useRef } from 'react';
import { useProgress } from '@/contexts/ProgressContext';

const TICK_MS = 5000; // опрос каждые 5 c
const IDLE_MS = 90_000; // 90 c без ввода → простой, не считаем
const FLUSH_SEC = 60; // флашить накопленное в прогресс ~раз в минуту

/**
 * Трекер экранного времени практики (хаб «Твоя практика» · 24 JUN 2026).
 * Пока вкладка видима, в фокусе и есть ввод (простой не дольше 90 c) — копит секунды
 * и периодически флашит в `progress.achievements.activityByDay` через `addScreenTime`.
 * Питает `activityMinByDay` в practiceStats (кольцо дня / хитмап / стрик).
 * Монтируется ОДИН раз на корне приложения. SSR/jsdom-safe (guard на window/document).
 */
export function useScreenTime(activeTicketId?: string | null): void {
  const { addScreenTime } = useProgress();
  const accumRef = useRef(0);
  const lastInputRef = useRef(Date.now());
  // Активный тикет (доска · 11 JUL 2026): секунды дополнительно копятся в
  // achievements.activityByTicket. Ref, чтобы интервал не перезапускался.
  const ticketRef = useRef<string | null>(activeTicketId ?? null);
  const flushRef = useRef<(final?: boolean) => void>(() => {});

  // Смена открытого тикета → флаш накопленного НА СТАРЫЙ тикет до переключения.
  useEffect(() => {
    if (ticketRef.current !== (activeTicketId ?? null)) {
      flushRef.current(true);
      ticketRef.current = activeTicketId ?? null;
    }
  }, [activeTicketId]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const markInput = () => {
      lastInputRef.current = Date.now();
    };
    const inputs: ReadonlyArray<keyof WindowEventMap> = [
      'pointerdown',
      'pointermove',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
    ];
    inputs.forEach((e) => window.addEventListener(e, markInput, { passive: true }));

    const flush = (final = false) => {
      const sec = accumRef.current;
      if (sec > 0 && (final || sec >= FLUSH_SEC)) {
        accumRef.current = 0;
        addScreenTime(sec, ticketRef.current);
      }
    };
    flushRef.current = flush;

    const isActive = () =>
      document.visibilityState === 'visible' &&
      (typeof document.hasFocus === 'function' ? document.hasFocus() : true) &&
      Date.now() - lastInputRef.current < IDLE_MS;

    const id = window.setInterval(() => {
      if (isActive()) {
        accumRef.current += TICK_MS / 1000;
        flush();
      }
    }, TICK_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush(true);
    };
    const onBlur = () => flush(true);
    const onBeforeUnload = () => flush(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.clearInterval(id);
      inputs.forEach((e) => window.removeEventListener(e, markInput));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('beforeunload', onBeforeUnload);
      flush(true);
    };
  }, [addScreenTime]);
}
